import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { injectSlice, State } from '@betty/toolkit/state';
import LimitsService from '../services/LimitsService';
import { Limits } from './limits.types';
import { LimitType } from '../services/LimitsService.schemas';
import { UpdatedValues } from '../components/PeriodInput/PeriodInput.types';
import { ApiError } from '@betty/toolkit/api';

export const fetchLImits = createAsyncThunk('limits/fetch', async () => {
  const res = await LimitsService.getLimits();

  return res;
});

export const updateLimits = createAsyncThunk(
  'limits/update',
  async (limits: UpdatedValues[], { rejectWithValue }) => {
    try {
      const res = await LimitsService.updateLimits({
        data: { limits },
      });
      return res;
    } catch (e) {
      if (e instanceof ApiError) {
        return rejectWithValue(e);
      }

      throw e;
    }
  },
);

const initialState: Limits = {
  data: null,
  dataState: 'idle',
  updateLimitsDataState: 'idle',
};

const limits = createSlice({
  name: 'limits',
  initialState,
  reducers: {
    resetUpdateLimitsDataState: (state) => {
      state.updateLimitsDataState = 'idle';
    },
    clearLimitsData: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchLImits.pending, (state) => {
      if (state.dataState === 'fulfilled') {
        state.dataState = 'refreshing';
      } else {
        state.dataState = 'pending';
      }
    });
    builder.addCase(fetchLImits.fulfilled, (state, action) => {
      state.dataState = 'fulfilled';

      state.data = action.payload.limits;
    });
    builder.addCase(fetchLImits.rejected, (state) => {
      state.dataState = 'rejected';
    });
    builder.addCase(updateLimits.fulfilled, (state) => {
      state.updateLimitsDataState = 'fulfilled';
    });
    builder.addCase(updateLimits.pending, (state) => {
      state.updateLimitsDataState = 'pending';
    });
    builder.addCase(updateLimits.rejected, (state) => {
      state.updateLimitsDataState = 'rejected';
    });
  },
});

export const withLimits = injectSlice({
  reducerPath: 'limits',
  reducer: limits.reducer,
});

export const { resetUpdateLimitsDataState, clearLimitsData } = limits.actions;

export const selectLimits = withLimits.selector((state) => state.limits.data);

export const selectFilteredLimitsByType = (type: LimitType) =>
  createSelector(
    (state: State) => state.limits?.data,
    (data) => {
      if (!data) {
        return null;
      } else {
        return data.filter((item) => item.type === type);
      }
    },
  );

export const selectLimitsDataState = withLimits.selector(
  (state) => state.limits.updateLimitsDataState,
);

declare global {
  interface LazyLoadedSlices {
    limits: ReturnType<typeof limits.reducer>;
  }
}
